import Box from '@mui/material/Box'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useLazyFetchConfiguredServiceEventsQuery } from 'api/configured-service/configuredServiceCrud'
import { useCSDetailsURLData } from 'api/configured-service/useCSDetailsURLData'
import ActivityCell from 'components/common/activity/ActivityCell'
import ActivityHistoryActions from 'components/common/activity/ActivityHistoryActions'
import HHAccordion from 'components/common/HHAccordion'
import { useActivity } from 'components/configured-service/accordions/ActivityAccordion/ActivityProvider'
import useFilterHandlers from 'components/filter/useFilterHandlers'
import usePersistedFilterModel from 'components/filter/usePersistedFilterModel'
import T from 'T'

import type { GridColDef, GridFilterModel } from '@mui/x-data-grid-pro'
import type { ActivityRow } from 'utils/activity/ActivityTreeBuilder'

// usePersistedFilterModel is untyped .js — drop the cast when it migrates.
type PersistedFilterModel = [GridFilterModel, (model: GridFilterModel) => void]

const COLUMN_VISIBILITY = {
  field: false,
  oldValue: false,
  newValue: false,
  modifiedBy: false,
  modifiedAt: false,
  changeType: false,
  rev: false,
}

type ConfiguredServiceActivityAccordionProps = {
  serviceId?: string
  hideHeader?: boolean
}

const ConfiguredServiceActivityAccordion = ({ serviceId, hideHeader = false }: ConfiguredServiceActivityAccordionProps) => {
  const apiRef = useGridApiRef()
  const { id: urlId } = useCSDetailsURLData()
  const id = serviceId ?? urlId
  const [fetchCSEvents] = useLazyFetchConfiguredServiceEventsQuery()
  const { treeData = [], isLoading = false } = useActivity() ?? {}

  const [filterModel, setFilterModel] = usePersistedFilterModel('ACTIVITY_ACCORDION_FILTER_MODEL') as PersistedFilterModel
  const { handleFilterModelChange } = useFilterHandlers(filterModel, setFilterModel, true)

  const columns = useMemo<GridColDef<ActivityRow>[]>(
    () => [
      { field: 'id', flex: 1, renderCell: ActivityCell, disableExport: true },
      { field: 'field', type: 'string', filterable: true },
      { field: 'modifiedBy', type: 'string', filterable: true },
      { field: 'modifiedAt', type: 'string', filterable: true },
      { field: 'oldValue', type: 'string', filterable: true },
      { field: 'newValue', type: 'string', filterable: true },
      { field: 'changeType', type: 'string' },
    ],
    []
  )

  const getRowHeight = useCallback(() => 'auto' as const, [])
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (id) fetchCSEvents({ id })
  }, [id, fetchCSEvents])

  const dataGrid = (
    <DataGridPro
      disableColumnMenu
      disableColumnResize
      disableColumnSelector
      disableDensitySelector
      apiRef={apiRef}
      columnHeaderHeight={0}
      columns={columns}
      columnVisibilityModel={COLUMN_VISIBILITY}
      filterModel={filterModel}
      getRowHeight={getRowHeight}
      loading={isLoading}
      rows={treeData}
      slotProps={{ panel: { anchorEl: filterAnchorEl } }}
      onFilterModelChange={handleFilterModelChange}
    />
  )

  // Nested inside the service accordion, which owns the header, so the actions ride above the grid.
  if (hideHeader)
    return (
      <>
        <Box display="flex" justifyContent="flex-end" px={2}>
          <ActivityHistoryActions apiRef={apiRef} onFilterAnchorChange={setFilterAnchorEl} />
        </Box>
        {dataGrid}
      </>
    )

  return (
    <HHAccordion
      isExpandCollapseHeightSame
      isExpanded
      showCustomActions
      withNewColors
      customActionContent={<ActivityHistoryActions apiRef={apiRef} onFilterAnchorChange={setFilterAnchorEl} />}
      showActionIcon={false}
      summary={T.ACTIVITY_HISTORY}
    >
      {dataGrid}
    </HHAccordion>
  )
}

export default ConfiguredServiceActivityAccordion
